import { useMutation } from '@tanstack/react-query';
import { useId, useState, type JSX } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { PlatformAdminRole } from '@/lib/permissions';

import { RoleChoice } from './RoleChoice';
import { invitePlatformAdmin, type PlatformAdminInvitation } from '../api/platform-admins.graphql';
import styles from '../pages/AdminsPage.module.scss';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface Props {
  onInvited: (invitation: PlatformAdminInvitation) => void;
  onCancel: () => void;
}

/**
 * OS-1717 — alta de un administrador por invitación. El nuevo admin define su contraseña y enrola
 * el MFA obligatorio con el link; hasta entonces no puede iniciar sesión.
 */
export function InviteAdminDialog({ onInvited, onCancel }: Props): JSX.Element {
  const emailId = useId();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PlatformAdminRole | null>(null);
  const mutation = useMutation({
    mutationFn: () => invitePlatformAdmin(email.trim(), role ?? 'SUPPORT_AGENT'),
    onSuccess: onInvited,
  });

  return (
    <ConfirmDialog
      title="Invitar administrador"
      confirmLabel="Crear invitación"
      pending={mutation.isPending}
      confirmDisabled={!EMAIL.test(email.trim()) || role === null}
      error={mutation.error?.message ?? null}
      onConfirm={() => mutation.mutate()}
      onCancel={onCancel}
    >
      <label className={styles.field} htmlFor={emailId}>
        Email *
        <input
          id={emailId}
          type="email"
          value={email}
          maxLength={254}
          autoComplete="off"
          placeholder="nombre@opssynk.com"
          onChange={(e) => setEmail(e.target.value)}
        />
      </label>
      <RoleChoice value={role} onChange={setRole} />
      <p>
        Se genera un link de un solo uso válido por 72 horas. Con él, la persona define su contraseña
        y configura el MFA antes de entrar.
      </p>
    </ConfirmDialog>
  );
}
